import React, { useState, useEffect } from 'react';
import { AppLayout } from '../layouts/AppLayout';
import { api, BACKEND_URL } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { BidHealthBadge, CompetitivePositionBar } from '../components/BidHealth';
import { 
  PlusCircle, 
  Trash2, 
  Calculator,
  Loader2,
  AlertTriangle,
  Download,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';

export default function NewBidPage() {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [org, setOrg] = useState(null);
  const [trades, setTrades] = useState([]);
  
  const [formData, setFormData] = useState({
    project_type: '',
    trade_type: '',
    submitted_price: '',
    cost_items: [
      { category: 'labour', quantity: '', unit_cost: '', total_cost: 0 },
      { category: 'materials', quantity: 1, unit_cost: '', total_cost: 0 },
      { category: 'subcontract', quantity: 1, unit_cost: '', total_cost: 0 }
    ]
  });

  useEffect(() => {
    const init = async () => {
      try {
        const [orgRes, tradesRes] = await Promise.all([
          api.get('/organisations/current'),
          api.get('/trades'),
        ]);
        setOrg(orgRes.data);
        setTrades(tradesRes.data.trades || []);
        
        // Set default trade from org
        const orgTrade = orgRes.data.organisation?.trade_code || 'electrical';
        
        // Pre-fill labour rate from settings
        const labourRate = orgRes.data.organisation?.settings?.default_labour_rate || 45;
        setFormData(prev => ({
          ...prev,
          trade_type: orgTrade,
          cost_items: prev.cost_items.map(item => 
            item.category === 'labour' 
              ? { ...item, unit_cost: labourRate }
              : item
          )
        }));
      } catch (error) {
        console.error('Failed to fetch org/trades', error);
      }
    };
    
    init();
  }, []);

  const updateCostItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.cost_items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Recalculate total_cost
      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].unit_cost) || 0;
      newItems[index].total_cost = qty * rate;
      
      return { ...prev, cost_items: newItems };
    });
    
    // Clear analysis when inputs change
    setAnalysisResult(null);
  };

  const addCostItem = () => {
    setFormData(prev => ({
      ...prev,
      cost_items: [...prev.cost_items, { category: '', quantity: 1, unit_cost: '', total_cost: 0 }]
    }));
  };

  const removeCostItem = (index) => {
    setFormData(prev => ({
      ...prev,
      cost_items: prev.cost_items.filter((_, i) => i !== index)
    }));
    setAnalysisResult(null);
  };

  const runAnalysis = async () => {
    const price = parseFloat(formData.submitted_price);
    if (!price || price <= 0) {
      toast.error('Please enter a valid submitted price');
      return;
    }
    
    const validItems = formData.cost_items.filter(item => 
      item.category && item.total_cost > 0
    );
    
    if (validItems.length === 0) {
      toast.error('Please add at least one cost item');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await api.post('/analysis/sanity-check', {
        cost_items: validItems.map(item => ({
          category: item.category,
          quantity: parseFloat(item.quantity) || 0,
          unit_cost: parseFloat(item.unit_cost) || 0,
          total_cost: item.total_cost
        })),
        submitted_price: price,
        project_type: formData.project_type,
        trade_type: formData.trade_type || null,
      });
      
      setAnalysisResult(res.data);
      toast.success('Analysis complete');
    } catch (error) {
      toast.error('Analysis failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const validItems = formData.cost_items.filter(item => 
        item.category && item.total_cost > 0
      );
      
      const response = await api.post('/analysis/sanity-check/report', {
        cost_items: validItems.map(item => ({
          category: item.category,
          quantity: parseFloat(item.quantity) || 0,
          unit_cost: parseFloat(item.unit_cost) || 0,
          total_cost: item.total_cost
        })),
        submitted_price: parseFloat(formData.submitted_price),
        project_type: formData.project_type,
        trade_type: formData.trade_type || null,
      }, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'BidSentry_SanityCheck_Report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report', error);
      toast.error('Failed to generate report');
    } finally {
      setDownloading(false);
    }
  };

  const totalCost = formData.cost_items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
  const submittedPrice = parseFloat(formData.submitted_price) || 0;
  const quickMargin = submittedPrice > 0 ? ((submittedPrice - totalCost) / submittedPrice * 100) : 0;

  const { margin_result, sensitivity_result, risk_assessment, bid_health, competitive_position, underbid_flags, ai_recommendations } = analysisResult || {};

  // Sensitivity chart data
  const sensitivityData = sensitivity_result?.variations?.filter(v => v.type === 'materials').map(v => ({
    name: `${v.variation_pct > 0 ? '+' : ''}${v.variation_pct}%`,
    margin: v.margin_pct
  })) || [];

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="new-bid-container">
        {/* Header */}
        <div>
          <h1 className="page-title">Bid Sanity Check</h1>
          <p className="text-slate-500">Analyse a new bid before you submit</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <div className="space-y-6">
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="section-title text-lg">Cost Items</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.cost_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      {index === 0 && <Label className="text-xs">Category</Label>}
                      <Input
                        value={item.category}
                        onChange={(e) => updateCostItem(index, 'category', e.target.value)}
                        placeholder="Category"
                        className="capitalize"
                        data-testid={`cost-category-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-xs">Qty/Hours</Label>}
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateCostItem(index, 'quantity', e.target.value)}
                        placeholder="0"
                        data-testid={`cost-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-3">
                      {index === 0 && <Label className="text-xs">Unit Cost (£)</Label>}
                      <Input
                        type="number"
                        value={item.unit_cost}
                        onChange={(e) => updateCostItem(index, 'unit_cost', e.target.value)}
                        placeholder="0.00"
                        data-testid={`cost-rate-${index}`}
                      />
                    </div>
                    <div className="col-span-3">
                      {index === 0 && <Label className="text-xs">Total (£)</Label>}
                      <div className="h-10 px-3 flex items-center bg-slate-100 rounded-sm font-mono text-sm">
                        £{item.total_cost.toLocaleString()}
                      </div>
                    </div>
                    <div className="col-span-1">
                      {formData.cost_items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCostItem(index)}
                          className="text-red-500 hover:text-red-700 h-10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCostItem}
                  data-testid="add-cost-item-btn"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Cost Item
                </Button>
                
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Direct Cost</span>
                    <span className="font-mono font-bold text-lg">£{totalCost.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="section-title text-lg">Bid Price</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Trade Type</Label>
                  <select
                    value={formData.trade_type}
                    onChange={(e) => {
                      setFormData({ ...formData, trade_type: e.target.value });
                      setAnalysisResult(null);
                    }}
                    className="mt-2 w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    data-testid="trade-type-select"
                  >
                    <option value="">Select trade...</option>
                    {trades.map(t => (
                      <option key={t.trade_code} value={t.trade_code}>{t.trade_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Project Type (optional)</Label>
                  <Input
                    value={formData.project_type}
                    onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                    placeholder="e.g. Commercial, Residential, Industrial"
                    className="mt-2"
                    data-testid="project-type-input"
                  />
                </div>
                
                <div>
                  <Label>Submitted Price (£)</Label>
                  <Input
                    type="number"
                    value={formData.submitted_price}
                    onChange={(e) => {
                      setFormData({ ...formData, submitted_price: e.target.value });
                      setAnalysisResult(null);
                    }}
                    placeholder="0.00"
                    className="mt-2 text-lg font-mono"
                    data-testid="submitted-price-input"
                  />
                </div>
                
                {submittedPrice > 0 && (
                  <div className="p-4 bg-slate-50 rounded-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Quick Margin (pre-overhead)</span>
                      <span className={`font-mono font-bold text-lg ${
                        quickMargin >= 15 ? 'text-green-600' :
                        quickMargin >= 0 ? 'text-orange-500' : 'text-red-600'
                      }`}>
                        {quickMargin.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}
                
                <Button
                  onClick={runAnalysis}
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="run-analysis-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Calculator className="w-5 h-5 mr-2" />
                      Run Analysis
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {analysisResult ? (
              <>
                {/* BID HEALTH BADGE - Primary Visual Anchor */}
                {bid_health && (
                  <div className="flex justify-end">
                    <BidHealthBadge
                      score={bid_health.score}
                      level={bid_health.level}
                      color={bid_health.color}
                      interpretation={bid_health.interpretation}
                      size="large"
                    />
                  </div>
                )}

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="card-industrial">
                    <CardContent className="pt-6">
                      <p className="kpi-label">Gross Margin</p>
                      <p className={`kpi-value ${
                        margin_result?.gross_margin_pct >= 15 ? 'text-green-600' :
                        margin_result?.gross_margin_pct >= 0 ? 'text-orange-500' : 'text-red-600'
                      }`}>
                        {margin_result?.gross_margin_pct?.toFixed(1)}%
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="card-industrial">
                    <CardContent className="pt-6">
                      <p className="kpi-label">Profit</p>
                      <p className={`kpi-value ${margin_result?.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        £{margin_result?.profit?.toLocaleString()}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Competitive Position Band */}
                {competitive_position && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="section-title text-lg">Competitive Position</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CompetitivePositionBar
                        position={competitive_position.position}
                        positionPercent={competitive_position.position_percent}
                        zone={competitive_position.zone}
                        zoneLabel={competitive_position.zone_label}
                        deviationText={competitive_position.deviation_text}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Warnings */}
                {underbid_flags?.length > 0 && (
                  <Card className="card-industrial border-orange-200 bg-orange-50/50">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        <span className="font-semibold text-orange-700">Warnings</span>
                      </div>
                      <ul className="space-y-2">
                        {underbid_flags.map((flag, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-orange-800">
                            <span className={`inline-block w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${
                              flag.severity === 'critical' ? 'bg-red-500' : 'bg-orange-500'
                            }`} />
                            {flag.message}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Sensitivity Chart */}
                <Card className="card-industrial">
                  <CardHeader>
                    <CardTitle className="section-title text-lg">Material Cost Sensitivity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={sensitivityData}>
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} domain={['dataMin - 5', 'dataMax + 5']} />
                          <Tooltip 
                            contentStyle={{ background: '#0f172a', border: 'none', color: '#fff' }}
                            formatter={(v) => [`${v}%`, 'Margin']}
                          />
                          <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
                          <Bar dataKey="margin" radius={[2, 2, 0, 0]}>
                            {sensitivityData.map((entry, i) => (
                              <Cell 
                                key={i} 
                                fill={entry.margin >= 15 ? '#10b981' : entry.margin >= 0 ? '#f97316' : '#ef4444'} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Risk spread: ±{sensitivity_result?.risk_spread?.toFixed(1)}%
                    </p>
                  </CardContent>
                </Card>

                {/* Recommendations */}
                {ai_recommendations && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="section-title text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Recommendations
                        <Badge variant="outline" className="text-xs">AI-Assisted</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="whitespace-pre-line text-sm text-slate-600">
                        {ai_recommendations.content}
                      </div>
                      <p className="text-xs text-slate-400 mt-4 italic">
                        {ai_recommendations.disclaimer}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Download Report Button */}
                <Button
                  onClick={handleDownloadReport}
                  variant="outline"
                  className="w-full"
                  disabled={downloading}
                  data-testid="download-sanity-report-btn"
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF Report
                </Button>
              </>
            ) : (
              <Card className="card-industrial">
                <CardContent className="py-16 text-center">
                  <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Enter your costs and bid price, then run analysis</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
